import type {
  AdyenLabels,
  SalesforcePaymentsCheckoutActions,
  SalesforcePaymentsCheckoutConfig,
  SalesforcePaymentsExpressConfig,
} from '../../types/config';
import { SfPaymentMethodComponent } from '../sfp-payment-method-component';
import type { SfPaymentMethodComponentConstructor } from '../sfp-payment-method-component';
import type { BillingDetails, PaymentIntentRequest, ShippingDetails } from '../../types/paymentRequest';
import type { Component } from '../../components/component';
import type { PaymentMethodElement, PaymentMode, VendorAccount } from '../../types/paymentMethodSet';
import { PaymentMethodType } from '../../types/paymentMethodSet';
import type { SetupResponse } from '../../types/paymentResponse';
import { ResponseCode, type PaymentResponse } from '../../types/paymentResponse';
import {
  createFormElement,
  createHeaderElement,
  deepMerge,
  DEFAULT_LABELS,
  removeUndefinedProperties,
  getEnhancedReturnUrl,
  label,
  adyenAmount,
} from '../../util/utility';
import type { ThemeRule } from '../../types/theme';
import './adyen-styles.css';
import type { VendorPaymentMethodElement } from '../../types/component';
import type { MetadataReader } from '../../util/metadata-reader';

export const ADYEN_DEFAULT_LABELS: AdyenLabels = {
  'adyen.bankAccountTitle': 'Bank Account Details',
  'adyen.usBankAccountHolderName': 'Account Holder',
  'adyen.usBankAccountHolderPlaceholder': 'Enter a name...',
  'adyen.usBankAccountNumber': 'Account Number',
  'adyen.usBankAccountRoutingNumber': 'Bank Routing Number',
  'adyen.usBankAccountHolderNameErrorMessage': 'Enter a valid account holder name',
  'adyen.usBankAccountNumberErrorMessage': 'Enter a valid account number and try again',
  'adyen.usBankAccountRoutingNumberErrorMessage': 'Enter a valid routing number and try again',
  'adyen.usSavedBankAccount': 'ACH Account Details',
  'adyen.sepaDebitHolderName': 'Account Holder',
  'adyen.sepaDebitHolderPlaceholder': 'Enter a name...',
  'adyen.bacsAccountHolderName': 'Account Holder Name',
  'adyen.bacsAccountHolderNameErrorMessage': 'Enter a valid account holder name.',
  'adyen.bacsAccountNumber': 'Account Number',
  'adyen.bacsAccountNumberErrorMessage': 'Enter a valid account number.',
  'adyen.bacsSortCode': 'Sort Code',
  'adyen.bacsSortCodeErrorMessage': 'Enter a valid sort code.',
  'adyen.bacsEmailAddress': 'Email',
  'adyen.bacsAmountDeductionConsent': 'I agree to the deduction of the total amount from my bank account.',
  'adyen.bacsAuthorizeConsent':
    'I confirm that the account is in my name and I am the sole signatory authorized to conduct Direct Debit transactions on this account.',
  'adyen.allFieldsRequired': 'All fields are required unless marked otherwise.',
  'adyen.paymentSuccessful': 'Payment was completed',
  'adyen.paymentRefused': 'Payment refused',
  'adyen.paymentFailed': 'Payment failed',
  'adyen.paymentFailedUnknownError': "We couldn't process your payment. Try again later.",
  'adyen.errorTitle': 'Error',
  'adyen.savedPaymentMethodDefault': '{0} (Default)',
  'adyen.creditCardNameOnCard': 'Name on card',
  'adyen.creditCardNameOnCardErrorMessage': 'Enter name as shown on card',
};

const ADYEN_TITLE_LABEL_MAP = new Map<string, string>([
  [PaymentMethodType.CARD, 'creditCardTitle'],
  [PaymentMethodType.US_BANK_ACCOUNT, 'usBankAccountTitle'],
  [PaymentMethodType.IDEAL, 'idealTitle'],
  [PaymentMethodType.EPS, 'epsTitle'],
  [PaymentMethodType.SEPA_DEBIT, 'sepaDebitTitle'],
  [PaymentMethodType.GOOGLEPAY, 'googlePayTitle'],
  [PaymentMethodType.APPLEPAY, 'applePayTitle'],
  [PaymentMethodType.AFTERPAY_CLEARPAY, 'afterpayClearpayTitle'],
  [PaymentMethodType.BACS_DEBIT, 'bacsDebitTitle'],
  [PaymentMethodType.KLARNA, 'klarnaPayLaterTitle'],
  [PaymentMethodType.KLARNA_PAY_NOW, 'klarnaPaynowTitle'],
  [PaymentMethodType.KLARNA_ACCOUNT, 'klarnaAccountTitle'],
  [PaymentMethodType.BANCONTACT_MOBILE, 'bancontactMobileTitle'],
  [PaymentMethodType.BANCONTACT, 'bancontactCardTitle'],
  [PaymentMethodType.TWINT, 'twintTitle'],
]);

const ADYEN_ICON_LABEL_MAP = new Map<string, string>([
  [PaymentMethodType.CARD, 'creditCardIconTitle'],
  [PaymentMethodType.US_BANK_ACCOUNT, 'usBankAccountIconTitle'],
  [PaymentMethodType.IDEAL, 'idealIconTitle'],
  [PaymentMethodType.EPS, 'epsIconTitle'],
  [PaymentMethodType.SEPA_DEBIT, 'sepaDebitIconTitle'],
  [PaymentMethodType.GOOGLEPAY, 'googlePayIconTitle'],
  [PaymentMethodType.APPLEPAY, 'applePayIconTitle'],
  [PaymentMethodType.AFTERPAY_CLEARPAY, 'afterpayClearpayIconTitle'],
  [PaymentMethodType.BACS_DEBIT, 'bacsDebitIconTitle'],
  [PaymentMethodType.KLARNA, 'klarnaIconTitle'],
  [PaymentMethodType.KLARNA_PAY_NOW, 'klarnaIconTitle'],
  [PaymentMethodType.KLARNA_ACCOUNT, 'klarnaIconTitle'],
  [PaymentMethodType.BANCONTACT_MOBILE, 'bancontactIconTitle'],
  [PaymentMethodType.BANCONTACT, 'bancontactIconTitle'],
  [PaymentMethodType.TWINT, 'twintIconTitle'],
]);

const ADYEN_REDIRECT_LABEL_MAP = new Map<string, string>([
  [PaymentMethodType.IDEAL, 'idealRedirect'],
  [PaymentMethodType.EPS, 'epsRedirect'],
  [PaymentMethodType.GOOGLEPAY, 'googlePayRedirect'],
  [PaymentMethodType.APPLEPAY, 'applePayRedirect'],
  [PaymentMethodType.AFTERPAY_CLEARPAY, 'afterpayClearpayRedirect'],
  [PaymentMethodType.KLARNA, 'klarnaRedirect'],
  [PaymentMethodType.KLARNA_PAY_NOW, 'klarnaRedirect'],
  [PaymentMethodType.KLARNA_ACCOUNT, 'klarnaRedirect'],
  [PaymentMethodType.BANCONTACT_MOBILE, 'bancontactRedirect'],
  [PaymentMethodType.TWINT, 'twintRedirect'],
]);

const ADYEN_FAILURE_RESPONSE_CODES = ['Refused', 'Error', 'Cancelled'];

export class SfpAdyenWeb extends SfPaymentMethodComponent {
  TYPE: PaymentMethodType;
  ELEMENT: PaymentMethodElement;
  REQUIRES_PAY_BUTTON = true;
  REQUIRES_BILLING_DETAILS = true;
  REQUIRES_BILLING_EMAIL = false;
  REQUIRES_BILLING_PHONE = false;
  APPLICABLE_CURRENCIES = ['ALL'];

  vendorAccount: VendorAccount;
  paymentRequest: PaymentIntentRequest;

  parentComponent: Component;
  webComponentElement: HTMLElement;

  webComponent: any;

  billingDetails: BillingDetails;
  shippingDetails: ShippingDetails;
  paymentResolve: any;

  key: string;
  sessionId: string;
  sessionData: string;
  allowedPaymentMethods: any;
  configuration: any;
  checkout: any;
  paymentIntentResponse: any;
  gatewayId: any;
  type: any;
  isSavedPaymentMethodsApplicable: boolean;
  locale: string;
  environment: string;
  isAddSpm: boolean;
  isValid: boolean;
  conditionalAvailabilityPaymentMethods: string[];

  private paymentDetails: Record<string, string> = {};

  async initialize(): Promise<boolean> {
    this.ELEMENT = this.paymentMethod.paymentMethodElement;
    this.TYPE = this.paymentMethod.paymentMethods[0].paymentMethodType;
    this.vendorAccount = this.paymentMethod.vendorAccount;
    this.key = this.vendorAccount.config.key;
    this.environment = this.vendorAccount.isLive ? 'live' : 'test';
    this.locale = this.paymentRequest.locale;
    this.conditionalAvailabilityPaymentMethods = ['applepay', 'googlepay'];
    this.isValid = true;

    this.configuration = this.generateConfiguration();

    this.checkout = await this.vendorObject(this.configuration);

    const webComponentLabel = document.createElement('div');
    this.webComponentElement = document.createElement('div');
    const webComponentError = document.createElement('div');

    const paymentMethodMapping = this.metadataReader.getGatewayPaymentMethodMapping(
      this.TYPE,
      this.vendorAccount.vendor,
      this.paymentRequest.currency
    );

    // If the payment method mapping is not supported by the vendor account, do not proceed with the initialization
    if (
      paymentMethodMapping &&
      paymentMethodMapping !== PaymentMethodType.CARD &&
      !this.vendorAccount.config.paymentMethods?.paymentMethods?.some(
        (paymentMethod: any) => paymentMethod.type === paymentMethodMapping
      )
    ) {
      return Promise.resolve(false);
    }

    if (this.webComponent) {
      this.webComponent.unmount();
      this.webComponent.update();
    } else {
      this.webComponent = this.checkout.create(paymentMethodMapping);
    }

    if (this.conditionalAvailabilityPaymentMethods.includes(this.TYPE)) {
      await this.webComponent
        .isAvailable()
        .then((isAvailable: any) => {
          // If Available mount it in the dom
          if (isAvailable) {
            this.initializeWebComponent(webComponentLabel, webComponentError, paymentMethodMapping);
          }
        })
        .catch((err: any) => {
          throw new Error('Failed to load Adyen with error: ' + err);
        });
    } else {
      this.initializeWebComponent(webComponentLabel, webComponentError, paymentMethodMapping);
    }

    return Promise.resolve(true);
  }

  afterMount(): void {
    // Mount the web component after the wrapper element is added to the DOM
    this.webComponent.mount(this.webComponentElement);
  }

  async updateAmount(amount: number): Promise<void> {
    this.configuration.amount = {
      value: adyenAmount(this.paymentRequest.currency, amount),
      currency: this.paymentRequest.currency.toUpperCase(),
    };
    await this.checkout.update();
    this.webComponent.update({
      amount: this.configuration.amount,
    });
  }

  getPaymentMethodType(): PaymentMethodType {
    return this.TYPE;
  }

  hasError(): boolean {
    const isValidPMS = [
      PaymentMethodType.CARD,
      PaymentMethodType.US_BANK_ACCOUNT,
      PaymentMethodType.SEPA_DEBIT,
      PaymentMethodType.BACS_DEBIT,
      PaymentMethodType.EPS,
      PaymentMethodType.IDEAL,
      PaymentMethodType.KLARNA,
      PaymentMethodType.KLARNA_PAY_NOW,
      PaymentMethodType.KLARNA_ACCOUNT,
      PaymentMethodType.BANCONTACT,
      PaymentMethodType.BANCONTACT_MOBILE,
      PaymentMethodType.AFTERPAY_CLEARPAY,
    ];
    if (isValidPMS.includes(this.TYPE)) {
      this.isValid = !!this.webComponent.isValid;
      if (!this.isValid) {
        // We need to submit to show the errors inside adyen web component.
        this.webComponent.submit();
      }
    } else {
      // For adyen web components with no fields, by default this value is false.
      // We need it to be true so that we can re-direct to respective payment method.
      this.isValid = true;
    }
    return !this.isValid;
  }

  async handleConfirm(
    _createIntent: Function,
    billingDetails: BillingDetails,
    shippingDetails: ShippingDetails
  ): Promise<PaymentResponse> {
    this.billingDetails = billingDetails;
    this.shippingDetails = shippingDetails;
    const promise: Promise<any> = new Promise((resolve) => {
      this.paymentResolve = resolve;
    });
    if (this.hasError()) {
      const response = {
        responseCode: ResponseCode.FAILURE,
        data: {},
      };
      this.paymentResolve(response);
    } else {
      await this.webComponent.submit();
    }
    return promise;
  }

  generateConfiguration(): any {
    const styleObject = this.createAdyenInputStyles();
    const translations = this.createAdyenTranslations();
    const configuration = {
      clientKey: this.key,
      environment: this.environment,
      showPayButton: false,
      paymentMethodsConfiguration: {
        ach: {
          holderNameRequired: false,
          billingAddressRequired: false,
          enableStoreDetails: this.isSavedPaymentMethodsApplicable,
          showPayButton: false,
          styles: styleObject,
        },
        card: {
          styles: styleObject,
          brandsConfiguration: {
            visa: {
              icon: this.metadataReader.getVariantPaymentMethodIconUrl(
                this.vendorAccount.vendor,
                'visa',
                'Card',
                this.config.theme?.mode
              ),
            },
            mc: {
              icon: this.metadataReader.getVariantPaymentMethodIconUrl(
                this.vendorAccount.vendor,
                'mc',
                'Card',
                this.config.theme?.mode
              ),
            },
            amex: {
              icon: this.metadataReader.getVariantPaymentMethodIconUrl(
                this.vendorAccount.vendor,
                'amex',
                'Card',
                this.config.theme?.mode
              ),
            },
            discover: {
              icon: this.metadataReader.getVariantPaymentMethodIconUrl(
                this.vendorAccount.vendor,
                'discover',
                'Card',
                this.config.theme?.mode
              ),
            },
            diners: {
              icon: this.metadataReader.getVariantPaymentMethodIconUrl(
                this.vendorAccount.vendor,
                'diners',
                'Card',
                this.config.theme?.mode
              ),
            },
            jcb: {
              icon: this.metadataReader.getVariantPaymentMethodIconUrl(
                this.vendorAccount.vendor,
                'jcb',
                'Card',
                this.config.theme?.mode
              ),
            },
            cup: {
              icon: this.metadataReader.getVariantPaymentMethodIconUrl(
                this.vendorAccount.vendor,
                'cup',
                'Card',
                this.config.theme?.mode
              ),
            },
          },
          showBrandsUnderCardNumber: false,
          ...(this.isFraudCheckPath() && {
            onBinValue: (data: any): void => {
              if (data.binValue) {
                this.paymentDetails.bin = data.binValue;
              } else {
                this.paymentDetails.bin = undefined;
              }
            },
            onBinLookup: (data: any): void => {
              if (data.supportedBrands?.length) {
                this.paymentDetails.brand = data.supportedBrands[0];
              } else {
                this.paymentDetails.brand = undefined;
              }
              if (data.issuingCountryCode) {
                this.paymentDetails.issuingCountry = data.issuingCountryCode;
              } else {
                this.paymentDetails.issuingCountry = undefined;
              }
            },
            onFieldValid: (data: any): void => {
              if (data.fieldType === 'encryptedCardNumber') {
                this.paymentDetails.last4 = data.endDigits || undefined;
              }
              if (data.fieldType === 'encryptedExpiryMonth') {
                this.paymentDetails.expiryMonth = data.value || undefined;
              }
              if (data.fieldType === 'encryptedExpiryYear') {
                this.paymentDetails.expiryYear = data.value || undefined;
              }
            },
          }),
        },
        storedCard: {
          styles: styleObject,
        },
      },
      paymentMethodsResponse: this.vendorAccount.config.paymentMethods,
      locale: this.locale,
      translations: translations,
      onSubmit: async (state: any, _component: any): Promise<void> => {
        // In case web component is invalid, no need to make calls to further apis.
        if (this.isValid) {
          if (this.isAddSpm) {
            await this.handleSetupIntent(state.data);
          } else {
            await this.handleIntent(state.data);
          }
        }
      },
      onAdditionalDetails: async (state: any, _component: any): Promise<void> => {
        await this.handleAdditionalDetails(state.data);
      },
    };
    if (this.paymentRequest.amount && this.paymentRequest.currency) {
      return {
        ...configuration,
        amount: {
          value: adyenAmount(this.paymentRequest.currency, this.paymentRequest.amount),
          currency: this.paymentRequest.currency.toUpperCase(),
        },
      };
    }
    return configuration;
  }

  private isFraudCheckPath(): boolean {
    return this.config.options?.enableFraudCheck === true && this.metadataReader.fraudCheckSupported(this.TYPE);
  }

  createAdyenInputStyles(): any {
    if (!this.config?.theme) {
      return {};
    }

    function adyenStyle(themeRule: ThemeRule): any {
      if (!themeRule) {
        return {};
      }
      // Note backgroundColor and padding omitted below to be applied to wrapper element
      return removeUndefinedProperties({
        color: themeRule.color,
        fontFamily: themeRule['font-family'],
        fontSize: themeRule['font-size'],
        fontVariant: themeRule['font-variant'],
        fontWeight: themeRule['font-weight'],
        lineHeight: themeRule['line-height'],
        letterSpacing: themeRule['letter-spacing'],
        textShadow: themeRule['text-shadow'],
        transition: themeRule.transition,
      });
    }

    const designTokens = this.config.theme.designTokens ?? {};
    const rules = this.config.theme.rules ?? {};

    // Reset CSS
    const normal = 'normal';
    const none = 'none';

    // Adyen style objects
    const input = deepMerge(
      removeUndefinedProperties({
        backgroundColor: 'transparent', // Background color applied to wrapper element
        color: designTokens['color-text-default'],
        fontFamily: designTokens['font-family'],
        fontSize: designTokens['font-size-5'],
        fontSmoothing: 'auto',
        fontStyle: normal,
        fontVariant: normal,
        lineHeight: designTokens['line-height-text'],
        letterSpacing: normal,
        textShadow: none,
      }),
      adyenStyle(rules.input)
    );
    const inputInvalid = deepMerge(
      {
        ...input,
      },
      adyenStyle(rules.input?.invalid)
    );

    return {
      base: input,
      error: inputInvalid,
      placeholder: removeUndefinedProperties({
        color: designTokens['color-text-placeholder'],
      }),
    };
  }

  createAdyenTranslations(): any {
    const translationMap = {
      'creditCard.numberField.title': this.config.labels?.creditCardNumber
        ? this.config.labels.creditCardNumber
        : DEFAULT_LABELS.creditCardNumber,
      'creditCard.expiryDateField.title': this.config.labels?.creditCardExpiry
        ? this.config.labels.creditCardExpiry
        : DEFAULT_LABELS.creditCardExpiry,
      'creditCard.cvcField.title': this.config.labels?.creditCardCvc
        ? this.config.labels.creditCardCvc
        : DEFAULT_LABELS.creditCardCvc,
      'creditCard.cvcField.placeholder.4digits': this.config.labels?.creditCardCvc
        ? this.config.labels.creditCardCvc
        : DEFAULT_LABELS.creditCardCvc,
      'creditCard.cvcField.placeholder.3digits': this.config.labels?.creditCardCvc
        ? this.config.labels.creditCardCvc
        : DEFAULT_LABELS.creditCardCvc,
      'error.va.sf-cc-num.01': this.config.labels?.creditCardNumberErrorMessage
        ? this.config.labels.creditCardNumberErrorMessage
        : DEFAULT_LABELS.creditCardNumberErrorMessage,
      'error.va.sf-cc-num.02': this.config.labels?.creditCardNumberErrorMessage
        ? this.config.labels.creditCardNumberErrorMessage
        : DEFAULT_LABELS.creditCardNumberErrorMessage,
      'error.va.sf-cc-num.03': this.config.labels?.creditCardNumberErrorMessage
        ? this.config.labels.creditCardNumberErrorMessage
        : DEFAULT_LABELS.creditCardNumberErrorMessage,
      'error.va.sf-cc-num.04': this.config.labels?.creditCardNumberErrorMessage
        ? this.config.labels.creditCardNumberErrorMessage
        : DEFAULT_LABELS.creditCardNumberErrorMessage,
      'error.va.sf-cc-dat.01': this.config.labels?.creditCardExpiryErrorMessage
        ? this.config.labels.creditCardExpiryErrorMessage
        : DEFAULT_LABELS.creditCardExpiryErrorMessage,
      'error.va.sf-cc-dat.02': this.config.labels?.creditCardExpiryErrorMessage
        ? this.config.labels.creditCardExpiryErrorMessage
        : DEFAULT_LABELS.creditCardExpiryErrorMessage,
      'error.va.sf-cc-dat.03': '',
      'error.va.sf-cc-dat.04': this.config.labels?.creditCardExpiryErrorMessage
        ? this.config.labels.creditCardExpiryErrorMessage
        : DEFAULT_LABELS.creditCardExpiryErrorMessage,
      'error.va.sf-cc-dat.05': this.config.labels?.creditCardExpiryErrorMessage
        ? this.config.labels.creditCardExpiryErrorMessage
        : DEFAULT_LABELS.creditCardExpiryErrorMessage,
      'error.va.sf-cc-cvc.01': this.config.labels?.creditCardCvvErrorMessage
        ? this.config.labels.creditCardCvvErrorMessage
        : DEFAULT_LABELS.creditCardCvvErrorMessage,
      'error.va.sf-cc-cvc.02': this.config.labels?.creditCardCvvErrorMessage
        ? this.config.labels.creditCardCvvErrorMessage
        : DEFAULT_LABELS.creditCardCvvErrorMessage,
      'sepa.ibanNumber': this.config.labels?.sepaDebitIban
        ? this.config.labels.sepaDebitIban
        : DEFAULT_LABELS.sepaDebitIban,
      'sepaDirectDebit.ibanField.invalid': this.config.labels?.sepaDebitIbanErrorMessage
        ? this.config.labels.sepaDebitIbanErrorMessage
        : DEFAULT_LABELS.sepaDebitIbanErrorMessage,
      'sepa.ownerName': this.config.labels?.['adyen.sepaDebitHolderName']
        ? this.config.labels['adyen.sepaDebitHolderName']
        : ADYEN_DEFAULT_LABELS['adyen.sepaDebitHolderName'],
      'sepaDirectDebit.nameField.placeholder': this.config.labels?.['adyen.sepaDebitHolderPlaceholder']
        ? this.config.labels['adyen.sepaDebitHolderPlaceholder']
        : ADYEN_DEFAULT_LABELS['adyen.sepaDebitHolderPlaceholder'],
      'ach.accountHolderNameField.title': this.config.labels?.['adyen.usBankAccountHolderName']
        ? this.config.labels['adyen.usBankAccountHolderName']
        : ADYEN_DEFAULT_LABELS['adyen.usBankAccountHolderName'],
      'ach.accountHolderNameField.placeholder': this.config.labels?.['adyen.usBankAccountHolderPlaceholder']
        ? this.config.labels['adyen.usBankAccountHolderPlaceholder']
        : ADYEN_DEFAULT_LABELS['adyen.usBankAccountHolderPlaceholder'],
      'ach.accountNumberField.title': this.config.labels?.['adyen.usBankAccountNumber']
        ? this.config.labels['adyen.usBankAccountNumber']
        : ADYEN_DEFAULT_LABELS['adyen.usBankAccountNumber'],
      'ach.accountLocationField.title': this.config.labels?.['adyen.usBankAccountRoutingNumber']
        ? this.config.labels['adyen.usBankAccountRoutingNumber']
        : ADYEN_DEFAULT_LABELS['adyen.usBankAccountRoutingNumber'],
      'ach.bankAccount': this.config.labels?.['adyen.bankAccountTitle']
        ? this.config.labels['adyen.bankAccountTitle']
        : ADYEN_DEFAULT_LABELS['adyen.bankAccountTitle'],
      'ach.accountHolderNameField.invalid': this.config.labels?.['adyen.usBankAccountHolderNameErrorMessage']
        ? this.config.labels['adyen.usBankAccountHolderNameErrorMessage']
        : ADYEN_DEFAULT_LABELS['adyen.usBankAccountHolderNameErrorMessage'],
      'ach.accountNumberField.invalid': this.config.labels?.['adyen.usBankAccountNumberErrorMessage']
        ? this.config.labels['adyen.usBankAccountNumberErrorMessage']
        : ADYEN_DEFAULT_LABELS['adyen.usBankAccountNumberErrorMessage'],
      'error.va.sf-ach-num.01': this.config.labels?.['adyen.usBankAccountNumberErrorMessage']
        ? this.config.labels['adyen.usBankAccountNumberErrorMessage']
        : ADYEN_DEFAULT_LABELS['adyen.usBankAccountNumberErrorMessage'],
      'error.va.sf-ach-num.02': this.config.labels?.['adyen.usBankAccountNumberErrorMessage']
        ? this.config.labels['adyen.usBankAccountNumberErrorMessage']
        : ADYEN_DEFAULT_LABELS['adyen.usBankAccountNumberErrorMessage'],
      'ach.accountLocationField.invalid': this.config.labels?.['adyen.usBankAccountRoutingNumberErrorMessage']
        ? this.config.labels['adyen.usBankAccountRoutingNumberErrorMessage']
        : ADYEN_DEFAULT_LABELS['adyen.usBankAccountRoutingNumberErrorMessage'],
      'error.va.sf-ach-loc.01': this.config.labels?.['adyen.usBankAccountRoutingNumberErrorMessage']
        ? this.config.labels['adyen.usBankAccountRoutingNumberErrorMessage']
        : ADYEN_DEFAULT_LABELS['adyen.usBankAccountRoutingNumberErrorMessage'],
      'error.va.sf-ach-loc.02': this.config.labels?.['adyen.usBankAccountRoutingNumberErrorMessage']
        ? this.config.labels['adyen.usBankAccountRoutingNumberErrorMessage']
        : ADYEN_DEFAULT_LABELS['adyen.usBankAccountRoutingNumberErrorMessage'],
      'ach.savedBankAccount': this.config.labels?.['adyen.usSavedBankAccount']
        ? this.config.labels['adyen.usSavedBankAccount']
        : ADYEN_DEFAULT_LABELS['adyen.usSavedBankAccount'],
      'form.instruction': this.config.labels?.['adyen.allFieldsRequired']
        ? this.config.labels['adyen.allFieldsRequired']
        : ADYEN_DEFAULT_LABELS['adyen.allFieldsRequired'],
      storeDetails: this.config.labels?.savePaymentMethodForFutureUse
        ? this.config.labels.savePaymentMethodForFutureUse
        : DEFAULT_LABELS.savePaymentMethodForFutureUse,
      'creditCard.success': this.config.labels?.['adyen.paymentSuccessful']
        ? this.config.labels['adyen.paymentSuccessful']
        : ADYEN_DEFAULT_LABELS['adyen.paymentSuccessful'],
      'error.title': this.config.labels?.['adyen.errorTitle']
        ? this.config.labels['adyen.errorTitle']
        : ADYEN_DEFAULT_LABELS['adyen.errorTitle'],
      'error.subtitle.payment': this.config.labels?.['adyen.paymentFailed']
        ? this.config.labels['adyen.paymentFailed']
        : ADYEN_DEFAULT_LABELS['adyen.paymentFailed'],
      'error.subtitle.refused': this.config.labels?.['adyen.paymentRefused']
        ? this.config.labels['adyen.paymentRefused']
        : ADYEN_DEFAULT_LABELS['adyen.paymentRefused'],
      'error.message.unknown': this.config.labels?.['adyen.paymentFailedUnknownError']
        ? this.config.labels['adyen.paymentFailedUnknownError']
        : ADYEN_DEFAULT_LABELS['adyen.paymentFailedUnknownError'],
      'bacs.accountHolderName': this.config.labels?.['adyen.bacsAccountHolderName']
        ? this.config.labels['adyen.bacsAccountHolderName']
        : ADYEN_DEFAULT_LABELS['adyen.bacsAccountHolderName'],
      'bacs.accountHolderName.invalid': this.config.labels?.['adyen.bacsAccountHolderNameErrorMessage']
        ? this.config.labels['adyen.bacsAccountHolderNameErrorMessage']
        : ADYEN_DEFAULT_LABELS['adyen.bacsAccountHolderNameErrorMessage'],
      'bacs.accountNumber': this.config.labels?.['adyen.bacsAccountNumber']
        ? this.config.labels['adyen.bacsAccountNumber']
        : ADYEN_DEFAULT_LABELS['adyen.bacsAccountNumber'],
      'bacs.accountNumber.invalid': this.config.labels?.['adyen.bacsAccountNumberErrorMessage']
        ? this.config.labels['adyen.bacsAccountNumberErrorMessage']
        : ADYEN_DEFAULT_LABELS['adyen.bacsAccountNumberErrorMessage'],
      'bacs.bankLocationId': this.config.labels?.['adyen.bacsSortCode']
        ? this.config.labels['adyen.bacsSortCode']
        : ADYEN_DEFAULT_LABELS['adyen.bacsSortCode'],
      'bacs.bankLocationId.invalid': this.config.labels?.['adyen.bacsSortCodeErrorMessage']
        ? this.config.labels['adyen.bacsSortCodeErrorMessage']
        : ADYEN_DEFAULT_LABELS['adyen.bacsSortCodeErrorMessage'],
      'bacs.consent.amount': this.config.labels?.['adyen.bacsAmountDeductionConsent']
        ? this.config.labels['adyen.bacsAmountDeductionConsent']
        : ADYEN_DEFAULT_LABELS['adyen.bacsAmountDeductionConsent'],
      'bacs.consent.account': this.config.labels?.['adyen.bacsAuthorizeConsent']
        ? this.config.labels['adyen.bacsAuthorizeConsent']
        : ADYEN_DEFAULT_LABELS['adyen.bacsAuthorizeConsent'],
      shopperEmail: this.config.labels?.['adyen.bacsEmailAddress']
        ? this.config.labels['adyen.bacsEmailAddress']
        : ADYEN_DEFAULT_LABELS['adyen.bacsEmailAddress'],
      'creditCard.holderName': this.config.labels?.['adyen.creditCardNameOnCard']
        ? this.config.labels['adyen.creditCardNameOnCard']
        : ADYEN_DEFAULT_LABELS['adyen.creditCardNameOnCard'],
      'creditCard.holderName.invalid': this.config.labels?.['adyen.creditCardNameOnCardErrorMessage']
        ? this.config.labels['adyen.creditCardNameOnCardErrorMessage']
        : ADYEN_DEFAULT_LABELS['adyen.creditCardNameOnCardErrorMessage'],
    };
    const translations: any = {};
    translations[this.locale] = translationMap;
    return translations;
  }

  destroy(): void {
    this.webComponent?.unmount();
    this.webComponent = null;

    super.destroy();
  }

  async handleConfirmSetup(_createSetupIntent: Function, _billingDetails: BillingDetails): Promise<SetupResponse> {
    return Promise.reject(new Error('No Setup intent to handle.'));
  }

  async handleIntent(data: any): Promise<void> {
    const paymentData: {
      paymentMethod: string;
      returnUrl: string;
      browserInfo?: string;
      shippingDetails?: Record<string, any>;
      billingDetails?: Record<string, any>;
      lineItems?: Record<string, any>[];
      origin?: string;
      paymentDetails?: Record<string, string>;
    } = {
      paymentMethod: data.paymentMethod,
      returnUrl: getEnhancedReturnUrl(this.config.options.returnUrl, this.vendorAccount, this.billingDetails),
      shippingDetails: this.shippingDetails,
      billingDetails: this.billingDetails,
    };

    // Passing browserInfo is required for 3DS and redirect Payment methods else Payment will be refused
    if (data.browserInfo) {
      paymentData.browserInfo = data.browserInfo;
    }

    // Passing origin is required for 3DS methods
    paymentData.origin = window.location.origin;

    if (
      this.TYPE === PaymentMethodType.AFTERPAY_CLEARPAY ||
      this.TYPE === PaymentMethodType.KLARNA ||
      this.TYPE === PaymentMethodType.KLARNA_PAY_NOW ||
      this.TYPE === PaymentMethodType.KLARNA_ACCOUNT
    ) {
      //This is temporary and we'll eventually provide an interface for clients to pass in the full list of line items
      paymentData.lineItems = [
        {
          description: this.config.labels?.finalAmountTitle ?? DEFAULT_LABELS.finalAmountTitle,
          amountIncludingTax: adyenAmount(this.paymentRequest.currency, this.paymentRequest.amount),
        },
      ];
    }

    if (this.isFraudCheckPath()) {
      paymentData.paymentDetails = this.paymentDetails;
    }

    // Deep clone the paymentData object to make sure it is mutable because create intent function may execute functions on it
    const paymentDataCopy = JSON.parse(JSON.stringify(paymentData));

    // This makes call to payment-intent api, which can cause unexpected exception if data is incorrect at adyen's end.
    // We need to handle that gracefully.
    try {
      this.paymentIntentResponse = await (this.config.actions as SalesforcePaymentsCheckoutActions).createIntent(
        paymentDataCopy
      );

      // No response from payment intent means pre auth failed for the payment.
      if (
        this.paymentIntentResponse &&
        !this.paymentIntentResponse.error &&
        !ADYEN_FAILURE_RESPONSE_CODES.includes(this.paymentIntentResponse.resultCode)
      ) {
        if (this.paymentIntentResponse.action) {
          this.webComponent.handleAction(this.paymentIntentResponse.action);
        } else {
          this.type = this.TYPE.toString() == 'card' ? this.paymentIntentResponse.paymentMethod?.brand : this.TYPE;
          this.paymentResolve({
            responseCode: ResponseCode.SUCCESS,
            data: {
              paymentData: {
                uuid: this.paymentIntentResponse.guid,
                id: this.paymentIntentResponse.pspReference,
                paymentGatewayId: this.vendorAccount.gatewayId,
                type: this.type,
                isManualCapture: this.config.options.useManualCapture ? 'true' : 'false',
              },
              paymentToken: this.paymentIntentResponse.guid,
              shippingDetails: this.shippingDetails,
              billingDetails: this.billingDetails,
            },
          });
        }
      } else {
        this.paymentResolve({
          responseCode: ResponseCode.FAILURE,
          data: {
            error: 'Payment Failed',
          },
        });
        this.updateWebComponent();
      }
    } catch (err) {
      this.paymentResolve({
        responseCode: ResponseCode.FAILURE,
        data: {
          error: 'Payment Failed',
          status: err.status,
        },
      });
      this.updateWebComponent();
    }
  }

  async handleSetupIntent(_data: any): Promise<void> {
    return Promise.reject(new Error('No Setup intent to handle.'));
  }

  async handleAdditionalDetails(data: any): Promise<void> {
    await (this.config.actions as SalesforcePaymentsCheckoutActions).handleAdditionalDetails(data);
  }

  getTitle(): string {
    if (ADYEN_TITLE_LABEL_MAP.has(this.TYPE)) {
      return label(this.config.labels, ADYEN_TITLE_LABEL_MAP.get(this.TYPE));
    }
  }

  getIconTitle(): string {
    if (ADYEN_ICON_LABEL_MAP.has(this.TYPE)) {
      return label(this.config.labels, ADYEN_ICON_LABEL_MAP.get(this.TYPE));
    }
  }

  getRedirectLabel(): string {
    if (ADYEN_REDIRECT_LABEL_MAP.has(this.TYPE)) {
      return label(this.config.labels, ADYEN_REDIRECT_LABEL_MAP.get(this.TYPE));
    } else {
      return label(this.config.labels, 'redirectNotification');
    }
  }

  initializeWebComponent(webComponentLabel: any, webComponentError: any, paymentMethodMapping: string): any {
    const componentName = 'sfpp-payment-method-' + this.TYPE + '-web-component';
    const webComponentItem = createFormElement(
      webComponentLabel,
      this.webComponentElement,
      webComponentError,
      componentName
    );

    // Body
    const bodyElement = document.createElement('div');
    bodyElement.appendChild(webComponentItem);
    this.element = bodyElement;

    if (
      this.metadataReader.getGatewayPaymentInstrumentMode(
        this.vendorAccount.vendor,
        paymentMethodMapping,
        this.paymentMode
      )?.showRedirectLabel
    ) {
      // Create redirect label footer
      const redirect = document.createElement('div');
      redirect.className = 'sfpp-payment-method-' + this.TYPE + '-redirect';
      const redirectText = this.getRedirectLabel();
      redirect.innerHTML = redirectText;
      bodyElement.appendChild(redirect);
    }

    // Header
    // Determine variant based on payment method mapping for AFTERPAY_CLEARPAY
    let variant: string | undefined;
    if (this.TYPE === PaymentMethodType.AFTERPAY_CLEARPAY) {
      variant = paymentMethodMapping;
    }

    const iconSrc = this.metadataReader.getPaymentMethodIconUrl(this.TYPE, this.config.theme?.mode, variant);
    const iconTitle = this.getIconTitle();
    const header = this.getTitle();
    this.header = createHeaderElement(iconSrc, iconTitle, header);

    // Authorization Mandate for sepa debit
    if (this.TYPE === PaymentMethodType.SEPA_DEBIT) {
      const authorizationText = document.createElement('div');
      authorizationText.className = 'sfpp-form-element__control sfpp-payment-method-sepa_debit-mandate';
      authorizationText.innerHTML = label(this.config.labels, 'sepaDebitAuthorizationMandate');

      const authorizationTextFormElement = document.createElement('div');
      authorizationTextFormElement.className = 'sfpp-form-element sfpp-form-element_stacked';
      authorizationTextFormElement.appendChild(authorizationText);
      bodyElement.appendChild(authorizationTextFormElement);
    }
  }

  updateWebComponent(): void {
    // Secured fields become uneditable in adyen card component. This results in non-functional checkout page in case of error.
    // Update component to make it re-usable.
    if (
      this.metadataReader.getGatewayPaymentInstrumentMode(this.vendorAccount.vendor, this.TYPE, this.paymentMode)
        ?.resetInputIfFailure
    ) {
      this.webComponent.update();
    }
  }
}

export const createSfpAdyenWeb: SfPaymentMethodComponentConstructor = function (
  parentComponent: Component,
  vendorObject: any,
  elements: any,
  metadataReader: MetadataReader,
  config: SalesforcePaymentsExpressConfig | SalesforcePaymentsCheckoutConfig,
  paymentRequest: PaymentIntentRequest,
  paymentMethod: VendorPaymentMethodElement,
  paymentMode?: PaymentMode
) {
  return new SfpAdyenWeb(
    parentComponent,
    vendorObject,
    elements,
    metadataReader,
    config,
    paymentRequest,
    paymentMethod,
    paymentMode
  );
};
